import React, { ReactNode, JSX } from 'react';
import { Card, CardBody, CardSubtitle, CardTitle, Col, Row } from 'reactstrap';
import {
  ApiBusinessPartnerContact,
  ApiAddress,
  ApiCommunicationDetails,
  ApiAgent,
  ApiClientType,
  ApiGetBusinessPartnerResponse,
  ApiRole,
  ApiBaseData
} from '@ibe/api';
import fallback from '../../../_Translations/generated/business-partner-info.en.t.json';
import Keys from '../../../_Translations/generated/business-partner-info.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import { authService } from '../../Hooks/Contexts';
import PasswordChangeForm from '../PasswordChange/PasswordChangeForm';
import AgencyProfileAddressForm from './AgencyProfileAddressForm';
import AgencyProfileContactForm from './AgencyProfileContactForm';
import AgentProfileInformationForm from './AgentProfileInformationForm';
import PasswordChangeFormType from '../PasswordChange/PasswordChangeFormType';
import AgencyPreferences from './AgencyPreferences';

export interface AgencyInfoProps {
  businessPartner: ApiGetBusinessPartnerResponse;
  onUserUpdate: (agent: ApiAgent) => void;
  onCompanyUpdate: (agency: ApiBusinessPartnerContact) => void;
  onPasswordChange: (values: PasswordChangeFormType) => void;
  notification: ReactNode;
  countries: ApiBaseData[];
  languages: ApiBaseData[];
  currencies: ApiBaseData[];
  passwordComplexityInfo?: string;
}

const BusinessPartnerInfo = (props: AgencyInfoProps): JSX.Element => {
  const { role } = authService.state;
  const { t } = useTranslation('agency-info', fallback, Keys);

  const {
    businessPartner,
    onUserUpdate,
    onCompanyUpdate,
    onPasswordChange,
    notification,
    countries,
    languages,
    currencies,
    passwordComplexityInfo
  } = props;

  const { agent, agency } = businessPartner;

  const getUserRole = (): string => {
    switch (role) {
      case ApiRole.AGENCYOWNER:
        return t(Keys.agencyOwner);
      case ApiRole.AGENT:
        return t(Keys.agent);
      case ApiRole.CONSUMER:
        return t(Keys.consumer);
      default:
        return '';
    }
  };

  const getClientType = (): string => {
    switch (agency.type) {
      case ApiClientType.Agency:
        return t(Keys.agency);
      case ApiClientType.Customer:
        return t(Keys.customer);
      default:
        return '';
    }
  };
  const handleAddressUpdate = (address: ApiAddress): void => {
    onCompanyUpdate({
      adress: address
    });
  };
  const handleAgentProfileUpdate = (communicationDetails: ApiCommunicationDetails): void => {
    onUserUpdate({
      communicationDetails
    });
  };

  const handleContactUpdate = (contact: ApiBusinessPartnerContact): void => {
    onCompanyUpdate({
      website: contact.website,
      phone: contact.phone
    });
  };

  return (
    <>
      {notification}
      {agency && role === ApiRole.AGENCYOWNER && (
        <Card className="mb-3">
          <CardBody>
            <CardTitle tag="h5">{agency.agencyName}</CardTitle>
            <CardSubtitle tag="h6" className="mb-5 text-muted">
              {`${getClientType()} #${agency.clientNumber}`}
            </CardSubtitle>
            <CardSubtitle tag="h5">{t(Keys.agencyAddress)}</CardSubtitle>
            <hr />
            <div className="mb-5">
              <AgencyProfileAddressForm
                agency={agency}
                countries={countries}
                onSubmit={handleAddressUpdate}
              />
            </div>
            <CardSubtitle tag="h5">{t(Keys.contact)}</CardSubtitle>
            <hr />
            <div className="mb-5">
              <AgencyProfileContactForm agency={agency} onSubmit={handleContactUpdate} />
            </div>
            <CardSubtitle tag="h5">{t(Keys.preferences)}</CardSubtitle>
            <hr />
            <AgencyPreferences
              language={
                languages.find(language => language.code === agency.languageCode)?.description || ''
              }
              currency={
                currencies.find(currency => currency.code === agency.currencyCode)?.description ||
                ''
              }
            />
          </CardBody>
        </Card>
      )}
      <Card>
        <CardBody>
          <CardTitle tag="h5">{`${agent.firstName} ${agent.lastName}`}</CardTitle>
          <CardSubtitle tag="h6" className="mb-5 text-muted">
            {getUserRole()}
          </CardSubtitle>
          <CardSubtitle tag="h5">{t(Keys.information)}</CardSubtitle>
          <hr />
          <AgentProfileInformationForm agent={agent} onSubmit={handleAgentProfileUpdate} />
          <CardSubtitle tag="h5">{t(Keys.password)}</CardSubtitle>
          <hr />
          <Row>
            <Col md={6} className="font-weight-light">
              <PasswordChangeForm
                onSubmit={onPasswordChange}
                withOldPassword
                passwordComplexityInfo={passwordComplexityInfo}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};
export default BusinessPartnerInfo;
